import * as msal from '@azure/msal-browser'
import {computed, makeAutoObservable, runInAction} from 'mobx'
import {AccountInfo, TokenClaims} from '@azure/msal-common'
import {config, loaderStore} from '../index'
import {LocalStorageClear, LocalStorageGet, LocalStorageRemove, LocalStorageSet} from './Helpers'
import {PromiseCompletion} from './PromiseCompletion'
import {Roles} from "../AppConstants";

type AccountData = AccountInfo & {idTokenClaims?: TokenClaims & {
        [key: string]: string | number | string[] | object | undefined | unknown;
    }};

type JwtToken = {
    aio: string;
    amr: string[];
    aud: string;
    exp: number;
    family_name: string;
    given_name: string;
    iat: number;
    ipaddr: string;
    iss: string;
    name: string;
    email: string;
    extension_Role: string;
    nbf: number;
    nonce: string;
    oid: string;
    rh: string;
    roles: string[];
    sub: string;
    tid: string;
    unique_name: string;
    upn: string;
    uti: string;
    ver: string;
};

type UserInfo = {
    accessToken: string
    account: AccountData
}
const TOKEN_KEY = 'currentToken'
const USER_KEY = 'currentUser'

export class AuthService {
    msalInstance: msal.PublicClientApplication
    currentToken: string = ''
    error: string| null = null
    currentUser: AccountData | null = null
    userInfo: UserInfo | null = null
    isLoading: PromiseCompletion = new PromiseCompletion()
    private _tokenRefreshInterval: NodeJS.Timeout | null = null

    constructor() {
        makeAutoObservable(this)
        this.msalInstance = new msal.PublicClientApplication(config.msal.auth)
    }

    private _parseJwtToken (token: string): JwtToken | null {
        if (!token) return null;

        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
            atob(base64).split('').map((c: string) => {
                return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
            }).join('')
        );

        return JSON.parse(jsonPayload);
    }
    private _validateToken (token: string) {
        const payload = this._parseJwtToken(token);
        if (!payload)
            return false;
        const nowUtc = Date.now();
        const expireAt = new Date(payload.exp * 1000);
        return expireAt.getTime() > nowUtc;
    };

    @computed
    public get tokenInfo () {
        return this._parseJwtToken(this.currentToken);
    }

    @computed
    public get isTokenValid () {
        return !!this.currentToken && this._validateToken(this.currentToken);
    }

    public init() {
        void this._selectAccount()
    }

    public signIn() {
        const loginRequest = {
            scopes: ['openid', ...config.msal.api.b2cScopes],
        }
        loaderStore.showAppLoader()
        this.msalInstance.loginPopup(loginRequest).then(async response => {
            await this._handleResponse(response)
        }).catch(err => {
            if (err.errorCode === 'access_denied') {
                this.error = err.errorMessage.split('\r\n')[0];
            }
            LocalStorageClear()
            this._clearUserData()
        }).finally(() => {
            loaderStore.hideAppLoader()
        })
    }


    @computed
    public get userRole() {
        return JSON.parse(atob(this.currentToken.split('.')[1]))?.roles?.[0];
    }

    @computed
    public get isAdmin() {

        return this.userRole ? this.userRole === Roles.GlobalAdmin || this.userRole === Roles.Admin : false;
    }
    @computed
    public get isCompanyAdmin() {

        return this.userRole ? this.userRole === Roles.Admin : false;
    }

    @computed
    public get isGlobalAdmin() {

        return this.userRole ? this.userRole === Roles.GlobalAdmin : false;
    }

    @computed
    public get isGlobalSupport() {

        return this.userRole ? this.userRole === Roles.GlobalSupport : false;
    }


    public initTokenRefresh() {
        this.stopTokenAutoRefresh()
        this._tokenRefreshInterval = setTimeout(async () => {
            await this.refreshToken()
            this.initTokenRefresh();
        }, 30 * 60 * 1000)
    }

    public stopTokenAutoRefresh() {
        if (this._tokenRefreshInterval !== null) {
            clearInterval(this._tokenRefreshInterval)
        }
    }

    public async signOut(signOutUser?: AccountData) {
        const user = signOutUser || this.currentUser
        if (user) {
            const logoutRequest = {
                account: this.msalInstance.getAccountByHomeId(user.homeAccountId),
                postLogoutRedirectUri: window.location.origin,
                onRedirectNavigate: this._clearUserData,
            }
            loaderStore.showAppLoader()
            await this.msalInstance.logoutRedirect(logoutRequest)
        }
    }

    public async refreshToken() {
        if (this.currentUser) {
            const conf = {
                scopes: ['openid', ...config.msal.api.b2cScopes],
                account: this.currentUser,
                forceRefresh: true,
            }
            try {
                this.isLoading = new PromiseCompletion()
                const tokenRequestSilent = this.msalInstance.acquireTokenSilent(conf)
                this.isLoading.subscribe(tokenRequestSilent)
                const tokenSilent = await tokenRequestSilent
                await this._handleResponse(tokenSilent)
                return tokenSilent
            } catch (silentError) {
                this._clearUserData();
            }
        }
    }

    private async _setCurrentUser(userInfo: UserInfo) {
        runInAction(() => {
            this._setUser(userInfo.account)
            this._setToken(userInfo.accessToken)
        })
        try {
            if (!this.isTokenValid) {
                await this.refreshToken()
            }
            this.initTokenRefresh()
        } catch (e) {

        }
    }

    private _handleResponse = async (response: any) => {
        this.error = null;
        if (response !== null) {
            const {account, accessToken} = response;

            this.userInfo = {account, accessToken}
            await this._setCurrentUser(this.userInfo)
        }
    }

    private _setUser(user: AccountData | null) {
        this.currentUser = user
        user ? LocalStorageSet(USER_KEY, user) : LocalStorageRemove(USER_KEY)
    }

    private _setToken(token: string) {
        this.currentToken = token
        token ? LocalStorageSet(TOKEN_KEY, token) : LocalStorageRemove(TOKEN_KEY)
    }

    private _clearUserData = () => {
        this.userInfo = null
        this._setUser(null)
        this._setToken('')
        sessionStorage.removeItem("msal.interaction.status");
    }

    private async _selectAccount() {
        const currentAccounts = this.msalInstance.getAllAccounts()
        const token = LocalStorageGet<string>(TOKEN_KEY)
        if (!!currentAccounts.length && token && this._validateToken(token || '')) {
            this.userInfo = {account: currentAccounts[0] as AccountData, accessToken: token || ''}
            await this._setCurrentUser(this.userInfo)
        } else {
            this._clearUserData();
        }
    }
}
